/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { ITextFieldStyles } from '@fluentui/react/lib/TextField';
import * as React from 'react';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { noop } from 'src/core/utils/base';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { transformLabels } from 'src/luis/modules/labeling/utils/transformLabels';
import { UtteranceRowOptions } from 'src/luis/projects/utterances/components/UtteranceTable/UtteranceRowOptions';
import { useUtterance } from 'src/luis/projects/utterances/hooks/useUtterance';
import { UtterancePredictionsStore } from 'src/luis/projects/utterances/hooks/useUtterances';
import { EntityUtteranceTableViewOptions } from 'src/luis/verticals/entityDetails/components/entityExamples/EntityExamples';
import { Labeler, LabelerProps } from 'src/modules/labeling/components/Labeler';

export type EntityUtteranceRowProps = {
    /**
     * Utterance tokenized text as segments
     */
    utterancePredictionStore: UtterancePredictionsStore;

    /**
     * Indicates whether the row is in edit mode or not
     */
    isInEditMode: boolean;

    /**
     * Callback function fired when the utterance edit mode changed
     */
    onEdit: (val: boolean) => void;

    /**
     * Callback function fired when the utterance edit submitted
     */
    onSubmitEditInput: (text: string) => void;

    /**
     * Callback function for the delete action
     */
    onDelete: () => void;

    /**
     * Callback function for confirm the utterance entity predictions
     */
    onConfirmPrediction: () => void;

    /**
     * Wether the utterance's culture is RTL or not.
     */
    isRTL: boolean;

    /**
     * Utterance view options
     */
    viewOptions: EntityUtteranceTableViewOptions;

    /**
     * User Role to extract user actions.
     */
    userRole: string;
};

const { styleComponent } = getStylistV2('EntityUtteranceRow');
const theme = getScopedTheme('EntityUtteranceRow');

const StyledLabeler = styleComponent<LabelerProps>(Labeler)('StyledLabeler', {
    margin: '8px 6px 8px 0px'
});

const editUtteranceInputStyles = (): Partial<ITextFieldStyles> => ({
    ...{
        fieldGroup: {
            backgroundColor: theme.editInputBackgroundColor
        },
        root: { minHeight: '40px' }
    }
});

/**
 * The view of the utterance cell in the utterances table that contain:
 * 1. The utterance text itself or the editable input field if the edit mode is enabled.
 * 2. On hover buttons to support additional actions to be applied to the selected utterance.
 */
export const EntityUtteranceRow = React.memo((props: EntityUtteranceRowProps) => {
    const {
        utterancePredictionStore,
        onDelete,
        isInEditMode,
        onEdit,
        onSubmitEditInput,
        onConfirmPrediction,
        isRTL,
        viewOptions,
        userRole
    } = props;

    const [isHovering, setIsHovering] = React.useState(false);

    const utterance = useUtterance(utterancePredictionStore);

    const [utteranceInput, setUtteranceInput] = React.useState(utterancePredictionStore.utteranceStore.text);

    const { canWrite } = mapUserRoleToUserActions(userRole);

    const { labels, conflicts } = React.useMemo(
        () =>
            transformLabels({
                tokenizedText: utterance.tokenizedText,
                userLabels: utterance.labeledEntities,
                predictions: utterance.predictedEntities,
                config: {
                    enableConflicts: false,
                    hideRegexAndPrebuilt: viewOptions.hideRegexAndPrebuilt,
                    hidePredictions: viewOptions.hidePredictions,
                    hideSubEntities: viewOptions.hideSubEntities
                }
            }),
        [utterance, viewOptions.hideSubEntities, viewOptions.hideRegexAndPrebuilt, viewOptions.hidePredictions]
    );
    const labelerConfig = React.useMemo(
        () => ({
            isRTL,
            disabled: true,
            viewOptions: { hideNames: viewOptions.hideEntityNames, isBold: false },
            disableHoverBrackets: false
        }),
        [isRTL, viewOptions.hideEntityNames]
    );

    /**
     * Handle the utterance edit input keydown event.
     */
    const handleUtteranceEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();

        switch (e.key) {
            case LuisKeyCodes.Escape:
                onEdit(false);
                break;
            case LuisKeyCodes.Enter:
                if (utteranceInput === utterancePredictionStore.utteranceStore.text) {
                    onEdit(false);
                } else {
                    onSubmitEditInput(utteranceInput);
                }
                break;
            default:
        }
    };

    return (
        <Stack
            verticalAlign="start"
            verticalFill
            horizontal
            onMouseEnter={() => setIsHovering(true)}
            onFocus={() => setIsHovering(true)}
            onBlur={() => setIsHovering(false)}
            onMouseLeave={() => setIsHovering(false)}>
            <Stack.Item grow>
                {isInEditMode ? (
                    <LuisTextField
                        autoFocus
                        styles={editUtteranceInputStyles}
                        value={utteranceInput}
                        onChange={(_, newUtterance) => setUtteranceInput(newUtterance)}
                        onBlur={() => {
                            onEdit(false);
                        }}
                        onKeyDown={handleUtteranceEditKeyDown}
                    />
                ) : (
                    <StyledLabeler
                        config={labelerConfig}
                        linesTokens={[utterance.tokenizedText]}
                        text={utterance.text}
                        labels={labels}
                        conflicts={conflicts}
                        onSelect={noop}
                        onShapeClick={noop}
                        onTokenHover={noop}
                        onTokenHoverExit={noop}
                        onLabelResize={noop}
                    />
                )}
            </Stack.Item>
            {!isInEditMode && (
                <UtteranceRowOptions
                    isHidden={!canWrite || !isHovering}
                    isMoreOptionsButtonHidden
                    onConfirmPrediction={onConfirmPrediction}
                    onDelete={onDelete}
                    onEdit={() => onEdit(true)}
                    isConfirmPredictionsDisabled={!labels.some(l => l.renderedAs === 'box')}
                    intents={[]}
                    onAddToPatterns={noop}
                    onReassignIntent={noop}
                    isAddAsPatternDisabled
                />
            )}
        </Stack>
    );
});

EntityUtteranceRow.displayName = 'EntityUtteranceRow';
